import { Injectable } from '@angular/core';
import { EnvironmentLoaderService } from './environment-loader.service';
import { OAuthToken } from '../models/auth';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { routes } from '../app-routing.module';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private token: OAuthToken;

  private readonly ATTEMPTED_URL_KEY = 'attemptedUrl';
  private readonly DEFAULT_URL = '/';

  constructor(
    private environmentLoaderService: EnvironmentLoaderService,
    private oauthService: OAuthService,
    private router: Router,
  ) {}

  get authToken(): OAuthToken {
    return this.token;
  }

  get username(): string {
    return this.oauthService.getIdentityClaims()?.['nickname'];
  }

  get roles(): string[] {
    return this.token?.realm_access.roles;
  }

  get isSuperuser(): boolean {
    return this.hasRole('kundeservice_superbruger') || this.hasRole('Egis_KS_Superbruger') || this.hasRole('Egis_KS_Superbruger_TST');
  }

  get isAdministrator(): boolean {
    return this.hasRole('administrator') || this.hasRole('egis_admin') || this.hasRole('Egis_Admin_TST');
  }

  get isKundeservice(): boolean {
    return this.hasRole('kundeservice') || this.hasRole('Egis_KS') || this.hasRole('Egis_KS_TST');
  }

  get isSalg(): boolean {
    return this.hasRole('salg') || this.hasRole('Egis_Salg') || this.hasRole('salg') || this.hasRole('Egis_Salg_TST');
  }

  get isReadonly(): boolean {
    return this.hasRole('egis_read') || this.hasRole('Egis_Read') || this.hasRole('egis_read') || this.hasRole('Egis_Read_TST');
  }

  get isGDPR(): boolean {
    return this.hasRole('Egis_GDPR') || this.hasRole('Egis_GDPR_TST');
  }

  get canWrite(): boolean {
    return this.isAdministrator || this.isKundeservice || this.isSalg || this.isSuperuser;
  }

  get canRead(): boolean {
    return this.isAdministrator || this.isKundeservice || this.isSalg || this.isReadonly;
  }

  hasRole(allowedRoles: string): boolean {
    const rolesArray = allowedRoles.split(',');
    if (this.roles) {
      return this.roles.some((r) => rolesArray.includes(r));
    }
    return false;
  }

  public logout() {
    this.oauthService.logOut();
  }

  private get authConfiguration(): AuthConfig {
    const environment = this.environmentLoaderService.config;

    return new AuthConfig({
      issuer: environment.authIssuer,
      redirectUri: window.location.origin,
      clientId: environment.clientId,
      responseType: 'code',
      scope: 'openid profile email offline_access access_token:email',
      useSilentRefresh: false,
      timeoutFactor: 0.75,
      logoutUrl: environment.authIssuer + 'oidc/logout',
      showDebugInformation: false,
      customQueryParams: {
        audience: 'egis-egis-service',
      },
    });
  }

  private redirectToAttemptedUrl() {
    const redirectUrl = sessionStorage.getItem(this.ATTEMPTED_URL_KEY) || sessionStorage.getItem(this.DEFAULT_URL);
    sessionStorage.removeItem(this.ATTEMPTED_URL_KEY);
    this.router.navigateByUrl(redirectUrl);
  }

  private saveAttemptedUrl() {
    const attemptedPath = window.location.pathname;
    // Paths start with / but routes don't. This also makes sure our home url are never set, as it is also the default url.
    const cleanedPath = attemptedPath.startsWith(this.DEFAULT_URL) ? attemptedPath.slice(1) : attemptedPath;
    // Only save path if it is a valid route
    if (!!cleanedPath && routes.some((route) => cleanedPath.includes(route.path))) {
      sessionStorage.setItem(this.ATTEMPTED_URL_KEY, attemptedPath + window.location.search);
    }
  }

  public async init(): Promise<boolean> {
    this.oauthService.configure(this.authConfiguration);
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.setStorage(sessionStorage);
    this.saveAttemptedUrl();

    return this.oauthService
      .loadDiscoveryDocumentAndLogin()
      .then((loggedIn) => {
        if (loggedIn) {
          const accessToken = this.oauthService.getAccessToken();
          this.token = this.parseToken(accessToken);

          this.redirectToAttemptedUrl();
        }
        return loggedIn;
      })
      .catch((error) => {
        console.error('AuthService.init() - error', error);
        return false;
      });
  }

  private parseToken(accessToken: string): OAuthToken {
    try {
      const payload = accessToken?.split('.')?.[1];
      if (payload) {
        return JSON.parse(atob(payload));
      } else {
        throw new Error('Invalid token format');
      }
    } catch (error) {
      console.error('Failed to parse token:', error);
      return null;
    }
  }
}
